import { useState, useEffect } from "react";
import { Calendar, User, Mail, Phone, X, Plus, Ban, UserPlus, Search, Filter } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showManualBooking, setShowManualBooking] = useState(false);
  const [showBlockDates, setShowBlockDates] = useState(false);
  const [manualForm, setManualForm] = useState({
    property_id: "",
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    check_in: "",
    check_out: "",
    num_guests: 1,
    total_price: 0,
    booking_type: "manual"
  });
  const [blockForm, setBlockForm] = useState({
    property_id: "",
    check_in: "",
    check_out: "",
  });

  useEffect(() => {
    fetchBookings();
    fetchProperties();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleCreateManualBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bookings/manual`, manualForm);
      toast.success("Manual booking created");
      setShowManualBooking(false);
      setManualForm({
        property_id: "",
        guest_name: "",
        guest_email: "",
        guest_phone: "",
        check_in: "",
        check_out: "",
        num_guests: 1,
        total_price: 0,
        booking_type: "manual"
      });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    }
  };

  const handleCreateFreeBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bookings/manual`, { ...manualForm, booking_type: "free" });
      toast.success("Free booking created");
      setShowManualBooking(false);
      setManualForm({
        property_id: "",
        guest_name: "",
        guest_email: "",
        guest_phone: "",
        check_in: "",
        check_out: "",
        num_guests: 1,
        total_price: 0,
        booking_type: "manual"
      });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create free booking");
    }
  };

  const handleBlockDates = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bookings/manual`, {
        ...blockForm,
        guest_name: "BLOCKED",
        guest_email: "blocked@kspaceproperties.com",
        guest_phone: "N/A",
        num_guests: 0,
        total_price: 0,
        booking_type: "blocked"
      });
      toast.success("Dates blocked successfully");
      setShowBlockDates(false);
      setBlockForm({ property_id: "", check_in: "", check_out: "" });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block dates");
    }
  };

  const getStatusColor = (booking) => {
    if (booking.status === 'cancelled') return 'bg-red-100 text-red-800';
    if (booking.booking_type === 'blocked') return 'bg-slate-100 text-slate-800';
    if (booking.booking_type === 'free') return 'bg-purple-100 text-purple-800';
    switch (booking.payment_status) {
      case 'paid':
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'not_required':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (booking) => {
    if (booking.status === 'cancelled') return 'Cancelled';
    if (booking.booking_type === 'blocked') return 'Blocked';
    if (booking.booking_type === 'free') return 'Free Booking';
    return booking.payment_status;
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesFilter = filter === "all" || 
      (filter === "confirmed" && (booking.payment_status === "paid" || booking.payment_status === "confirmed")) ||
      (filter === "pending" && booking.payment_status === "pending") ||
      (filter === "cancelled" && booking.status === "cancelled") ||
      (filter === "blocked" && booking.booking_type === "blocked");
    
    const matchesSearch = !searchTerm || 
      booking.guest_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  return (
    <AdminLayout>
      <div data-testid="admin-bookings">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary">
            Bookings Management
          </h1>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowBlockDates(true)} 
              variant="outline"
              data-testid="block-dates-button"
            >
              <Ban className="w-4 h-4 mr-2" />
              Block Dates
            </Button>
            <Button 
              onClick={() => setShowManualBooking(true)} 
              className="bg-accent hover:bg-accent/90"
              data-testid="create-booking-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-bookings-input"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-bookings-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bookings</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.map((booking, index) => (
            <Card key={booking.id} data-testid={`booking-item-${index}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-primary">{booking.guest_name}</h3>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${getStatusColor(booking)}`} data-testid={`booking-status-${index}`}>
                        {getStatusLabel(booking)}
                      </span>
                      {booking.booking_type && booking.booking_type !== 'regular' && booking.booking_type !== 'blocked' && (
                        <span className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 font-medium">
                          {booking.booking_type}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-accent" />
                        <span>{booking.check_in} to {booking.check_out}</span>
                      </div>
                      {booking.booking_type !== 'blocked' && (
                        <>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-accent" />
                            <span>{booking.num_guests} guest{booking.num_guests !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-accent" />
                            <span>{booking.guest_email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-accent" />
                            <span>{booking.guest_phone}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-3">
                    {booking.booking_type !== 'blocked' && (
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          {booking.total_price > 0 ? `£${booking.total_price}` : 'Free'}
                        </div>
                        <div className="text-xs text-slate-500">
                          ID: {booking.id.slice(0, 8)}
                        </div>
                      </div>
                    )}

                    {booking.status !== 'cancelled' && (
                      <div className="flex gap-2 flex-wrap justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => handleCancelBooking(booking.id)}
                          data-testid={`cancel-booking-btn-${index}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredBookings.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-600" data-testid="no-bookings-message">
                {searchTerm || filter !== "all" 
                  ? "No bookings match your search criteria." 
                  : "No bookings yet. They will appear here once guests start booking!"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Manual Booking Dialog */}
        <Dialog open={showManualBooking} onOpenChange={setShowManualBooking}>
          <DialogContent className="max-w-lg" data-testid="manual-booking-dialog">
            <DialogHeader>
              <DialogTitle>Create Booking</DialogTitle>
              <DialogDescription>
                Add a manual or complimentary booking
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateManualBooking} className="space-y-4">
              <div>
                <Label htmlFor="mb-property">Property</Label>
                <Select 
                  value={manualForm.property_id} 
                  onValueChange={(val) => setManualForm({...manualForm, property_id: val})}
                >
                  <SelectTrigger data-testid="mb-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="mb-name">Guest Name</Label>
                  <Input
                    id="mb-name"
                    value={manualForm.guest_name}
                    onChange={(e) => setManualForm({...manualForm, guest_name: e.target.value})}
                    required
                    data-testid="mb-guest-name"
                  />
                </div>
                <div>
                  <Label htmlFor="mb-phone">Phone</Label>
                  <Input
                    id="mb-phone"
                    value={manualForm.guest_phone}
                    onChange={(e) => setManualForm({...manualForm, guest_phone: e.target.value})}
                    required
                    data-testid="mb-guest-phone"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="mb-email">Email</Label>
                <Input
                  id="mb-email"
                  type="email"
                  value={manualForm.guest_email}
                  onChange={(e) => setManualForm({...manualForm, guest_email: e.target.value})}
                  required
                  data-testid="mb-guest-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="mb-checkin">Check-in</Label>
                  <Input
                    id="mb-checkin"
                    type="date"
                    value={manualForm.check_in}
                    onChange={(e) => setManualForm({...manualForm, check_in: e.target.value})}
                    required
                    data-testid="mb-checkin"
                  />
                </div>
                <div>
                  <Label htmlFor="mb-checkout">Check-out</Label>
                  <Input
                    id="mb-checkout"
                    type="date"
                    value={manualForm.check_out}
                    onChange={(e) => setManualForm({...manualForm, check_out: e.target.value})}
                    required
                    data-testid="mb-checkout"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="mb-guests">Guests</Label>
                  <Input
                    id="mb-guests"
                    type="number"
                    min="1"
                    value={manualForm.num_guests}
                    onChange={(e) => setManualForm({...manualForm, num_guests: parseInt(e.target.value)})}
                    data-testid="mb-num-guests"
                  />
                </div>
                <div>
                  <Label htmlFor="mb-price">Total Price (£)</Label>
                  <Input
                    id="mb-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualForm.total_price}
                    onChange={(e) => setManualForm({...manualForm, total_price: parseFloat(e.target.value)})}
                    data-testid="mb-total-price"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleCreateFreeBooking}
                  data-testid="create-free-booking-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Free Booking
                </Button>
                <Button 
                  type="submit" 
                  className="bg-accent hover:bg-accent/90"
                  data-testid="create-manual-booking-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Booking
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Block Dates Dialog */}
        <Dialog open={showBlockDates} onOpenChange={setShowBlockDates}>
          <DialogContent data-testid="block-dates-dialog">
            <DialogHeader>
              <DialogTitle>Block Dates</DialogTitle>
              <DialogDescription>
                Block a date range to prevent guest bookings
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleBlockDates} className="space-y-4">
              <div>
                <Label>Property</Label>
                <Select 
                  value={blockForm.property_id} 
                  onValueChange={(val) => setBlockForm({...blockForm, property_id: val})}
                >
                  <SelectTrigger data-testid="bd-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={blockForm.check_in}
                    onChange={(e) => setBlockForm({...blockForm, check_in: e.target.value})}
                    required
                    data-testid="bd-from-date"
                  />
                </div>
                <div>
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={blockForm.check_out}
                    onChange={(e) => setBlockForm({...blockForm, check_out: e.target.value})}
                    required
                    data-testid="bd-to-date"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="confirm-block-dates-btn">
                  <Ban className="w-4 h-4 mr-1" />
                  Block Dates
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBookings;
